import { useEffect, useRef, useState, type FC } from "react";
import { useLocation } from "react-router-dom";

import { useTitleFor } from "../chat/useTitleFor";
import { artifactKey, useCaseIde } from "../context/CaseIdeContext";
import { useCaseData, type CaseData } from "../data/CaseDataContext";
import { OPEN_SEARCH_PARAM, parseOpenParam, type OpenRequest } from "../routes";
import { openValue, writeSearchParams } from "../urlState";

const ALWAYS_THERE = new Set([
  "settings",
  "plan",
  "time",
  "company",
  "workflows"
]);

/** Whether the linked artifact is on the case (a deleted draft is not). */
export const linkTargetExists = (
  request: OpenRequest,
  { fs, workspace }: Pick<CaseData, "fs" | "workspace">,
  individualId: string
): boolean => {
  if (ALWAYS_THERE.has(request.kind)) return true;
  if (request.kind === "key_facts") return request.id === individualId;
  if (request.kind === "file" || request.kind === "card")
    return fs.nodes.some((n) => n.id === request.id && !n.is_deleted);
  if (request.kind === "packet") return true;
  if (
    request.kind === "draft" &&
    workspace.drafts.some((d) => d.draft_id === request.id)
  )
    return true;
  return workspace.sections.some((s) =>
    s.items.some(
      (i) => i.artifact.kind === request.kind && i.artifact.id === request.id
    )
  );
};

/**
 * Keeps `?open=kind:id` and the active tab in step. On load the URL wins over
 * the tabs remembered in localStorage: a linked tab that is already open (a
 * reload) comes to the front at once, so closing it sticks; anything else
 * opens once the files and workspace have loaded (a stale link is dropped
 * quietly). After that, every tab switch rewrites the param, so a reload or a
 * copied link lands on the same artifact. An in-app navigation to `?open=`
 * while the IDE is mounted (the router's location key changes) links again
 * the same way.
 */
export const OpenFromUrl: FC = () => {
  const { open, setActive, tabs, activeArtifact, individualId } = useCaseIde();
  const data = useCaseData();
  const titleFor = useTitleFor();
  const { key, search } = useLocation();
  const linked = useRef(parseOpenParam(window.location.search));
  const [resolved, setResolved] = useState(linked.current === null);
  const ready = !data.fs.isLoading && !data.workspace.isLoading;
  const linkedKey = linked.current && artifactKey(linked.current);
  const linkedOpen = tabs.some((t) => artifactKey(t) === linkedKey);

  const seenKey = useRef(key);
  useEffect(() => {
    if (seenKey.current === key) return;
    seenKey.current = key;
    const request = parseOpenParam(search);
    if (!request) return;
    linked.current = request;
    setResolved(false);
  }, [key, search]);

  useEffect(() => {
    const request = linked.current;
    if (resolved || !request || !(linkedOpen || ready)) return;
    if (linkedOpen) setActive(artifactKey(request));
    else if (linkTargetExists(request, data, individualId))
      open({ ...request, title: titleFor(request) });
    setResolved(true);
    // Once: when the linked tab is open, or else when the case's lists land.
  }, [resolved, ready, linkedOpen]);

  const activeValue = openValue(activeArtifact);
  useEffect(() => {
    if (resolved) writeSearchParams({ [OPEN_SEARCH_PARAM]: activeValue });
  }, [resolved, activeValue]);

  return null;
};
